import type { Setting } from '../../context/settings-context';
import { generateRandomId } from '../../lib/random';

export type OAuthPartnerId = 'brightpearl' | 'katana' | 'quickbooks' | 'xero';

export type OAuthPartner = {
  /** Interpolated into the callback copy. Not translated — these are brand names. */
  name: string;
  /**
   * Must stay identical to the redirect URIs registered with each provider's app config and to the
   * Vue router entries — a mismatch makes the provider reject the handshake before it starts.
   */
  redirectPath: string;
  /** localStorage key written by `startOAuthFlow` in the modal and read by the callback. */
  stateKey: string;
  /** Extra localStorage key cleared on callback (QuickBooks stashes its pending form payload here). */
  settingsKey?: string;
  /**
   * Settings persisted when the provider hands back a code. `null` = the query was unusable
   * (missing `code`, or partner-required `account`/`realmId`).
   */
  buildSettings(
    query: Record<string, string | undefined>,
    storedSettingsJson: string | null,
  ): Setting[] | null;
};

export const OAUTH: Record<OAuthPartnerId, OAuthPartner> = Object.freeze({
  brightpearl: {
    name: 'Brightpearl',
    redirectPath: '/configuration/integrations/oauth-brightpearl',
    stateKey: 'sparkBpOAuthState',
    buildSettings(query, _storedSettingsJson) {
      if (!query.code || !query.account) return null;
      return [
        { key: 'integrationBrightpearlOAuthCode', value: query.code },
        { key: 'integrationBrightpearlAccount', value: query.account },
      ];
    },
  },
  katana: {
    name: 'Katana',
    redirectPath: '/configuration/integrations/oauth-katana',
    stateKey: 'sparkKatanaOAuthState',
    buildSettings(query, _storedSettingsJson) {
      if (!query.code) return null;
      return [{ key: 'integrationKatanaOAuthCode', value: query.code }];
    },
  },
  xero: {
    name: 'Xero',
    redirectPath: '/configuration/integrations/oauth-xero',
    stateKey: 'sparkXeroOAuthState',
    buildSettings(query, _storedSettingsJson) {
      if (!query.code) return null;
      return [
        { key: 'integrationXero', value: true },
        { key: 'integrationXeroOAuthCode', value: query.code },
      ];
    },
  },
  quickbooks: {
    name: 'QuickBooks',
    redirectPath: '/integrations/partners/oauth/quickbooks',
    stateKey: 'sparkQuickBooksOAuthState',
    settingsKey: 'sparkQuickBooksSettings',
    buildSettings(query, storedSettingsJson) {
      if (!query.code || !query.realmId) return null;
      let selectedSettings: Setting[] = [];
      if (storedSettingsJson) {
        try {
          const parsed: unknown = JSON.parse(storedSettingsJson);
          selectedSettings = Array.isArray(parsed) ? (parsed as Setting[]) : [];
        } catch {
          return null;
        }
      }
      return [
        ...selectedSettings,
        { key: 'integrationQuickBooks', value: true },
        { key: 'integrationQuickBooksOAuthCode', value: query.code },
        { key: 'integrationQuickBooksRealmId', value: query.realmId },
      ];
    },
  },
});

export function buildOAuthRedirectUri(redirectPath: string): string {
  return `https://${window.location.hostname}${redirectPath}`;
}

/**
 * Build a provider's authorize URL with every parameter percent-encoded.
 *
 * Values here are user- or environment-supplied (account IDs, redirect URIs, space-separated
 * scopes), so hand-rolled interpolation would let a stray `/`, `?`, `#` or space truncate the
 * query string and drop the parameters the handshake depends on.
 */
export function buildOAuthAuthorizeUrl(endpoint: string, params: Record<string, string>): string {
  return `${endpoint}?${new URLSearchParams(params).toString()}`;
}

/** Generate an opaque OAuth state token (replaces `uuid()` v4). */
export function generateOAuthState(): string {
  return generateRandomId();
}
